/**
 * Analysis API
 *
 * Wraps `/api/v1/analysis/{analysisId}/*` endpoints — JSON results, report
 * exports (CSV / JSON / DOCX / PDF / XLSX / OpenXSAM / custom PDF), and
 * generated report items. The shared axios client has baseURL `/api/v1`.
 *
 * Blob downloads pass `{ responseType: 'blob' }` so axios returns the Blob
 * through the interceptor's `response.data` unwrap.
 */

import client from './client';

const BLOB = { responseType: 'blob' };

function withSections(path, sections, separator = '?') {
  if (!sections) return path;
  return `${path}${separator}sections=${encodeURIComponent(sections)}`;
}

/**
 * Fetch the complete analysis results JSON for the results page.
 * Mirrors AthenaBackendService.getAnalysisResults(analysisId).
 */
export async function getAnalysisResults(analysisId) {
  return client.get(`/analysis/${analysisId}/results`);
}

/**
 * Download the CSV report (in-memory generation).
 * Mirrors AthenaBackendService.downloadCsvReport(analysisId, sections).
 */
export async function downloadCsvReport(analysisId, sections = null) {
  return client.get(
    withSections(`/analysis/${analysisId}/csv`, sections),
    BLOB
  );
}

/**
 * Download the full results JSON as a Blob (`?download=true`).
 * Mirrors AthenaBackendService.downloadJsonReport(analysisId, sections).
 */
export async function downloadJsonReport(analysisId, sections = null) {
  const path = `/analysis/${analysisId}/results?download=true`;
  return client.get(withSections(path, sections, '&'), BLOB);
}

/**
 * Download the DOCX report.
 * Mirrors AthenaBackendService.downloadDocxReport(analysisId, sections).
 */
export async function downloadDocxReport(analysisId, sections = null) {
  return client.get(
    withSections(`/analysis/${analysisId}/docx`, sections),
    BLOB
  );
}

/**
 * Download the PDF report.
 * Mirrors AthenaBackendService.downloadPdfReport(analysisId, sections).
 */
export async function downloadPdfReport(analysisId, sections = null) {
  return client.get(
    withSections(`/analysis/${analysisId}/pdf`, sections),
    BLOB
  );
}

/**
 * Fetch the assembled Cybersecurity Case (ISO 21434 Clause 6).
 *
 * JSON, not a blob — the case is reviewed on screen before it is exported,
 * because its value is showing which argument legs are still unsupported.
 */
export async function getCybersecurityCase(analysisId) {
  return client.get(`/analysis/${analysisId}/cybersecurity-case`);
}

/**
 * Fetch the ranked Pentest Scope (E1-R1) for a completed analysis.
 *
 * JSON, not a blob — the scope is reviewed and trimmed on screen before it
 * becomes a work order.
 */
export async function getPentestScope(analysisId) {
  return client.get(`/analysis/${analysisId}/pentest-scope`);
}

/**
 * Download the XLSX report.
 * Mirrors AthenaBackendService.downloadXlsxReport(analysisId).
 */
export async function downloadXlsxReport(analysisId) {
  return client.get(`/analysis/${analysisId}/xlsx`, BLOB);
}

/**
 * Download the OpenXSAM XML export.
 * Mirrors AthenaBackendService.downloadOpenXsamReport(analysisId).
 */
export async function downloadOpenXsamReport(analysisId) {
  return client.get(`/analysis/${analysisId}/openxsam`, BLOB);
}

/**
 * Download a custom PDF report assembled from user-selected items.
 * Mirrors AthenaBackendService.downloadCustomPdfReport(analysisId, templateItems, metadata).
 */
export async function downloadCustomPdfReport(analysisId, templateItems, metadata = null) {
  const body = { items: templateItems };
  if (metadata) body.metadata = metadata;
  return client.post(`/analysis/${analysisId}/custom-pdf`, body, BLOB);
}

/**
 * Generate the per-section report items for a completed analysis.
 * Mirrors AthenaBackendService.generateReportItems(analysisId).
 */
export async function generateReportItems(analysisId) {
  return client.post(`/analysis/${analysisId}/report-items/generate`);
}

/**
 * Fetch previously generated report items.
 * Mirrors AthenaBackendService.getReportItems(analysisId).
 */
export async function getReportItems(analysisId) {
  return client.get(`/analysis/${analysisId}/report-items`);
}
